import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  ARCA_VAT_CONDITION_CONSUMIDOR_FINAL,
  ARCA_VAT_CONDITION_EXENTO,
  ARCA_VAT_CONDITION_MONOTRIBUTO,
  ARCA_VAT_CONDITION_RESPONSABLE_INSCRIPTO,
  ARCA_VOUCHER_TYPE_FACTURA_A,
  ARCA_VOUCHER_TYPE_FACTURA_B,
  ARCA_VOUCHER_TYPE_FACTURA_C,
  ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_A,
  ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_B,
  ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_C,
  ArcaAliquotType,
  arcaAliquotTypeOptions,
  ArcaBusinessVatCondition,
  arcaBusinessVatConditionOptions,
  ArcaDocumentType,
  arcaDocumentTypeOptions,
  ArcaVatCondition,
  arcaVatConditionOptions,
  ArcaVoucherType,
  arcaVoucherTypeOptions,
} from './arca.runtime-types';
import { ArcaVoucherLetter, BillingInformationArca, TaxVoucherArca } from './arca.types';
import {
  AliquotAmount,
  BillingInformation,
  TaxVoucher,
  TaxVoucherOperation,
} from '@pedix-workspace/utils';

export function isBillingInformationArca(
  billingInformation: BillingInformation | null | undefined,
): billingInformation is BillingInformationArca {
  return billingInformation?.provider === 'arca';
}

export function isTaxVoucherArca(
  taxVoucher: TaxVoucher | null | undefined,
): taxVoucher is TaxVoucherArca {
  return taxVoucher?.provider === 'arca';
}
/**
 * Convierte una fecha a formato YYYYMMDD como lo requiere ARCA/AFIP.
 * Usa la zona horaria local Argentina para consistencia en facturación.
 */
export function dateToArcaFormat(
  date: Date = new Date(),
  timeZone: string = 'America/Argentina/Buenos_Aires',
): string {
  const zonedDate = utcToZonedTime(date, timeZone);

  return format(zonedDate, 'yyyyMMdd');
}

export function getVoucherType(
  establishmentVatCondition: ArcaBusinessVatCondition,
  customerVatCondition: ArcaVatCondition,
  voucherType: 'bill' | 'credit',
): ArcaVoucherType {
  if (establishmentVatCondition === ARCA_VAT_CONDITION_RESPONSABLE_INSCRIPTO) {
    switch (customerVatCondition) {
      case ARCA_VAT_CONDITION_RESPONSABLE_INSCRIPTO:
        return voucherType === 'bill'
          ? ARCA_VOUCHER_TYPE_FACTURA_A
          : ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_A;
      case ARCA_VAT_CONDITION_MONOTRIBUTO:
        return voucherType === 'bill'
          ? ARCA_VOUCHER_TYPE_FACTURA_A
          : ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_A;
      case ARCA_VAT_CONDITION_EXENTO:
        return voucherType === 'bill'
          ? ARCA_VOUCHER_TYPE_FACTURA_B
          : ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_B;
      case ARCA_VAT_CONDITION_CONSUMIDOR_FINAL:
        return voucherType === 'bill'
          ? ARCA_VOUCHER_TYPE_FACTURA_B
          : ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_B;
      default:
        return voucherType === 'bill'
          ? ARCA_VOUCHER_TYPE_FACTURA_B
          : ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_B;
    }
  } else if (
    establishmentVatCondition === ARCA_VAT_CONDITION_MONOTRIBUTO ||
    establishmentVatCondition === ARCA_VAT_CONDITION_EXENTO
  ) {
    return voucherType === 'bill'
      ? ARCA_VOUCHER_TYPE_FACTURA_C
      : ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_C;
  }

  throw new Error('Unsupported VAT condition');
}

export function getVoucherLetter(voucherType: ArcaVoucherType): ArcaVoucherLetter {
  switch (voucherType) {
    case ARCA_VOUCHER_TYPE_FACTURA_A:
    case ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_A:
      return 'A' as const;
    case ARCA_VOUCHER_TYPE_FACTURA_B:
    case ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_B:
      return 'B' as const;
    case ARCA_VOUCHER_TYPE_FACTURA_C:
    case ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_C:
      return 'C' as const;
    default:
      throw new Error('Unsupported voucher type');
  }
}
export function getVoucherOperation(voucherType: ArcaVoucherType): TaxVoucherOperation {
  switch (voucherType) {
    case ARCA_VOUCHER_TYPE_FACTURA_A:
    case ARCA_VOUCHER_TYPE_FACTURA_B:
    case ARCA_VOUCHER_TYPE_FACTURA_C:
      return 'bill' as const;
    case ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_A:
    case ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_B:
    case ARCA_VOUCHER_TYPE_NOTA_DE_CREDITO_C:
      return 'credit' as const;
    default:
      throw new Error('Unsupported operation');
  }
}
export function getArcaVatConditionName(vatCondition: ArcaVatCondition): string {
  return arcaVatConditionOptions.find(option => option.id === vatCondition).name;
}
export function getArcaBusinessVatConditionName(vatCondition: ArcaBusinessVatCondition): string {
  return arcaBusinessVatConditionOptions.find(option => option.id === vatCondition).name;
}
export function getArcaDocumentTypeName(documentType: ArcaDocumentType): string {
  return (
    arcaDocumentTypeOptions.find(option => option.id === documentType).name ||
    'Documento sin especificar'
  );
}
export function getArcaVoucherTypeName(voucherType: ArcaVoucherType): string {
  return arcaVoucherTypeOptions.find(option => option.id === voucherType).name;
}
export function generateVoucherRef(pointOfSale: number, voucherNumber: number) {
  return [pointOfSale.toString().padStart(4, '0'), voucherNumber.toString().padStart(8, '0')].join(
    '-',
  );
}
export function getArcaAliquotValue(defaultAliquotType: ArcaAliquotType): AliquotAmount {
  return arcaAliquotTypeOptions.find(option => option.id === defaultAliquotType).value;
}
export function getArcaAliquotTypeFromValue(value: number): ArcaAliquotType {
  return arcaAliquotTypeOptions.find(option => option.value === value)?.id;
}
export function getSanitizedAmount(amount: number): number {
  return parseFloat(amount.toFixed(2));
}
